import { atom, type Getter, type Setter } from 'jotai';
import { assembleLayer, planBlocks } from '../model/assemble';
import { planView, type ViewState } from '../model/viewPlan';
import { blockPath, type BlockKey, type WindBlock } from '../tiles/format';
import { fetchBlock, fetchWindIndex } from '../windApi';
import {
  blocksAtom,
  clockAtom,
  forecastTimeAtom,
  loadStatusAtom,
  viewAtom,
  windFieldAtom,
  windIndexAtom,
} from './atoms';

/** The list of runs is checked again after this long, ms: models update every 1–6 hours. */
const INDEX_TTL_MS = 5 * 60_000;
/** Most blocks kept (each ~34 kB); the least recently used go first. */
const MAX_BLOCKS = 400;
/** Blocks arriving within this long are drawn together, ms: each redraw repaints the whole overlay. */
const REBUILD_MS = 100;

/** Blocks being fetched, by path, so moving the map on doesn't request them again. */
const pendingAtom = atom<ReadonlySet<string>>(new Set<string>());

/** Aborted when the forecast is hidden; requests otherwise run to the end, even if the view moved on. */
const controllerAtom = atom(new AbortController());

/** The index being fetched, shared by loads that need it at once. */
const indexRequestAtom = atom<Promise<unknown> | null>(null);

/** Recomputes what the map shows from the view, the loaded blocks and the time. */
function rebuild(get: Getter, set: Setter) {
  const view = get(viewAtom);
  const index = get(windIndexAtom)?.index;
  if (!view || !index) return;
  const blocks = get(blocksAtom);
  const blockOf = (key: BlockKey) => blocks.get(blockPath(key));
  const previous = new Map(get(windFieldAtom).layers.map((l) => [l.model, l]));
  const plans = planView(index, view, get(forecastTimeAtom) ?? get(clockAtom));
  set(windFieldAtom, { layers: plans.map((p) => assembleLayer(p, blockOf, previous.get(p.model.id))) });
}

let rebuildTimer: ReturnType<typeof setTimeout> | undefined;

function scheduleRebuild(get: Getter, set: Setter) {
  rebuildTimer ??= setTimeout(() => {
    rebuildTimer = undefined;
    rebuild(get, set);
  }, REBUILD_MS);
}

/** The cache with `block` added as most recently used, trimmed to MAX_BLOCKS. */
function withBlock(cache: ReadonlyMap<string, WindBlock>, path: string, block: WindBlock) {
  const next = new Map(cache);
  next.delete(path);
  next.set(path, block);
  for (const key of next.keys()) {
    if (next.size <= MAX_BLOCKS) break;
    next.delete(key);
  }
  return next;
}

/** Fetches the list of runs if missing or due for a check; a failed check keeps the old one. */
async function ensureIndex(get: Getter, set: Setter, now: number) {
  const current = get(windIndexAtom);
  if (current && now - current.fetched < INDEX_TTL_MS) return;
  let request = get(indexRequestAtom);
  if (!request) {
    request = fetchWindIndex(get(controllerAtom).signal)
      .then((index) => set(windIndexAtom, { index, fetched: Date.now() }))
      .finally(() => set(indexRequestAtom, null));
    set(indexRequestAtom, request);
  }
  try {
    await request;
  } catch (err) {
    if (!current) throw err;
  }
}

function setPending(set: Setter, paths: readonly string[], pending: boolean) {
  set(pendingAtom, (keys) => {
    const next = new Set(keys);
    for (const p of paths) {
      if (pending) next.add(p);
      else next.delete(p);
    }
    return next;
  });
}

/**
 * Shows the wind on `view` (or the last view), fetching the blocks it needs that aren't loaded, drawn as
 * they arrive. Also moves "now" along, so calling it on a timer keeps the shown time and the runs current.
 */
export const loadWindAtom = atom(null, async (get, set, view?: ViewState) => {
  const now = Date.now();
  // Whole minutes: the wind barely changes in one, and an unchanged clock doesn't redraw the overlay.
  set(clockAtom, now - (now % 60_000));
  if (view) set(viewAtom, view);
  const shown = get(viewAtom);
  if (!shown) return;
  const { signal } = get(controllerAtom);

  try {
    await ensureIndex(get, set, now);
  } catch (err) {
    if (signal.aborted) return;
    console.error(err);
    set(loadStatusAtom, { kind: 'error', error: err });
    return;
  }
  rebuild(get, set);

  const index = get(windIndexAtom)!.index;
  const blocks = get(blocksAtom);
  const pending = get(pendingAtom);
  // Coarse to fine: the base model draws first, regional detail comes on top.
  const missing = planView(index, shown, get(forecastTimeAtom) ?? get(clockAtom))
    .flatMap(planBlocks)
    .map((key) => ({ key, path: blockPath(key) }))
    .filter(({ path }) => !blocks.has(path) && !pending.has(path));
  if (missing.length === 0) {
    set(loadStatusAtom, { kind: 'idle' });
    return;
  }
  setPending(
    set,
    missing.map((m) => m.path),
    true,
  );

  const results = await Promise.allSettled(
    missing.map(async ({ key, path }) => {
      try {
        const block = await fetchBlock(key, signal);
        set(blocksAtom, (cache) => withBlock(cache, path, block));
        scheduleRebuild(get, set);
      } finally {
        if (!signal.aborted) setPending(set, [path], false);
      }
    }),
  );
  if (signal.aborted) return;
  const failure = results.find((r) => r.status === 'rejected');
  if (failure) console.error(failure.reason);
  set(loadStatusAtom, failure ? { kind: 'error', error: failure.reason } : { kind: 'idle' });
});

/** Stops loading, e.g. when the forecast is hidden. */
export const cancelWindAtom = atom(null, (get, set) => {
  get(controllerAtom).abort();
  set(controllerAtom, new AbortController());
  set(pendingAtom, new Set<string>());
  set(indexRequestAtom, null);
  set(loadStatusAtom, { kind: 'idle' });
});

/** Shows the forecast for a time (ms since the epoch), or null for now. */
export const setForecastTimeAtom = atom(null, (_get, set, time: number | null) => {
  set(forecastTimeAtom, time);
  void set(loadWindAtom);
});
